import {
  BrowserAuthError,
  InteractionRequiredAuthError,
  type AccountInfo,
  type Configuration,
  type IPublicClientApplication,
} from '@azure/msal-browser';
import { VITE_AZURE_AUTH_CLIENT_ID, VITE_AZURE_AUTH_TENANT_ID } from '@src/main/env/index';
import type { TokenProvider } from '@src/main/modules/common/common/infrastructure/http/axios';

export const msalConfig: Configuration = {
  auth: {
    clientId: VITE_AZURE_AUTH_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${VITE_AZURE_AUTH_TENANT_ID}`,
    redirectUri: '/',
    postLogoutRedirectUri: '/login',
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
};

export class MsalTokenProvider implements TokenProvider {
  private readonly instance: IPublicClientApplication;

  private readonly accounts: AccountInfo[];

  constructor(instance: IPublicClientApplication, accounts: AccountInfo[]) {
    this.instance = instance;
    this.accounts = accounts;
  }

  async getToken(): Promise<string> {
    const request = {
      scopes: [`${VITE_AZURE_AUTH_CLIENT_ID}/.default`],
      account:
        this.accounts.filter((account) => account.tenantId === VITE_AZURE_AUTH_TENANT_ID).at(0) ??
        this.accounts[0],
    };

    const authenticationResult = await this.instance
      .acquireTokenSilent(request)
      .catch(async (error) => {
        if (error instanceof InteractionRequiredAuthError) {
          // eslint-disable-next-line no-console
          console.log('Interaction required when acquiring token silently');
          return this.instance.acquireTokenRedirect(request);
        }
        if (error instanceof BrowserAuthError) {
          // eslint-disable-next-line no-console
          console.log('Browser auth error when acquiring token silently');
        }
        return null;
      });

    if (!authenticationResult) {
      throw new Error('Failed to acquire token');
    }

    return authenticationResult.accessToken;
  }
}
